import React, { useState } from "react";
import { format } from "date-fns";
import { FileJson, FileWarning, Play, Trash2, X } from "lucide-react";

import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import { Button } from "../../components/misc/Button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../components/misc/Dialog";
import PostVideoPlayer from "../../components/post/PostVideoPlayer";
import CaptionAuditDialog from "./CaptionAuditDialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/misc/Table";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../../components/misc/Pagination";
import Spinner from "../../components/misc/Spinner";
import { cn } from "../../utils/classMerger";
import {
    CaptionViolationRender,
    DISPOSITION_LABELS,
    REASON_DESCRIPTIONS,
    REASON_LABELS,
    SlideDisposition,
    SlideFailureReason,
} from "./types";

interface CaptionViolationsListProps {
    renders: CaptionViolationRender[];
    total: number;
    page: number;
    limit: number;
    loading: boolean;
    onPageChange: (page: number) => void;
    className?: string;
}

const ReasonChip: React.FC<{ reason: SlideFailureReason }> = ({ reason }) => (
    <span
        title={REASON_DESCRIPTIONS[reason]}
        className="mr-1 inline-block cursor-help rounded-full bg-secondary px-2 py-1 text-xs font-bold text-primary shadow-sm"
    >
        {REASON_LABELS[reason]}
    </span>
);

// What the never-ship policy did with this slide. Absent on renders that predate the
// policy — those failing captions actually shipped, so there is nothing to badge.
// "Video cancelled" is the more severe outcome, so it reads red; a drop is amber.
const DispositionBadge: React.FC<{ disposition?: SlideDisposition }> = ({
    disposition,
}) => {
    if (!disposition) return null;
    return (
        <span
            className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold",
                disposition === "video-failed"
                    ? "bg-[#d50000] text-white"
                    : "bg-[#f0c755] text-primary"
            )}
        >
            {DISPOSITION_LABELS[disposition]}
        </span>
    );
};

// The render-level outcome, summarised for its own column. The reader's first question is
// "did the video ship?", so the pill answers THAT (Shipped / Not shipped) and the line
// below it says what it cost — dropping a slide still ships a video, which the old
// "1 slide dropped" label left ambiguous. A render can mix dispositions, so this rolls
// them up: any "video-failed" means the whole video was cancelled and dominates; otherwise
// any "dropped" slide still shipped, minus those slides; a render with no dispositions
// predates the never-ship policy — those failing captions shipped as-is.
const RenderOutcome: React.FC<{
    slides: CaptionViolationRender["slides"];
}> = ({ slides }) => {
    const cancelled = slides.some((s) => s.disposition === "video-failed");
    const dropped = slides.filter((s) => s.disposition === "dropped").length;

    if (cancelled) {
        return (
            <div className="flex flex-col items-start gap-1">
                <span className="rounded-full bg-[#d50000] px-2 py-1 text-xs font-bold text-white">
                    Not shipped
                </span>
                <span className="text-[11px] text-gray-500">
                    video cancelled
                </span>
            </div>
        );
    }
    if (dropped > 0) {
        return (
            <div className="flex flex-col items-start gap-1">
                <span className="rounded-full bg-[#f0c755] px-2 py-1 text-xs font-bold text-primary">
                    Shipped
                </span>
                <span className="text-[11px] text-gray-500">
                    {dropped} slide{dropped === 1 ? "" : "s"} dropped
                </span>
            </div>
        );
    }
    // No policy action — the failing caption shipped as-is (this render predates the policy).
    return (
        <div
            className="flex flex-col items-start gap-1"
            title="These captions shipped — this render predates the never-ship policy."
        >
            <span className="text-xs font-semibold text-gray-500">Shipped</span>
            <span className="text-[11px] text-gray-400">
                as-is (pre-policy)
            </span>
        </div>
    );
};

const CaptionViolationsList: React.FC<CaptionViolationsListProps> = ({
    renders,
    total,
    page,
    limit,
    loading,
    onPageChange,
    className,
}) => {
    const [preview, setPreview] = useState<CaptionViolationRender | null>(null);
    // Null until a reviewer opens one — the audit record is fetched lazily, per render.
    const [auditId, setAuditId] = useState<string | null>(null);

    const pageCount = Math.max(1, Math.ceil(total / limit));
    const pages = Array.from({ length: pageCount }, (_, i) => i + 1).slice(
        Math.max(0, page - 3),
        Math.max(0, page - 3) + 5
    );

    return (
        <Card className={cn("mt-0", className)}>
            <CardHeader className="p-4 pb-2">
                <CardTitle className="text-left text-sm font-semibold">
                    Captions that failed validation
                </CardTitle>
                {/* Say what happens to these, so the Outcome column reads as expected. */}
                <p className="text-xs text-gray-500">
                    A caption still breaking a rule after the retry budget is dropped
                    from the video, or the whole video is cancelled — never shipped. One
                    row per render, with its failing captions grouped under it. Older rows
                    marked &ldquo;Shipped&rdquo; predate the never-ship policy.
                </p>
            </CardHeader>

            <CardContent className="p-4 pt-0">
                {loading ? (
                    <div className="flex justify-center py-10">
                        <Spinner />
                    </div>
                ) : renders.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-10 text-gray-500">
                        <FileWarning className="h-8 w-8" />
                        <span className="text-sm">
                            No captions shipped with violations in this window.
                        </span>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table className="min-w-[1240px]">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[120px]">
                                        When
                                    </TableHead>
                                    <TableHead className="w-[170px]">
                                        User
                                    </TableHead>
                                    <TableHead className="w-[200px]">
                                        Listing
                                    </TableHead>
                                    <TableHead>Bad captions</TableHead>
                                    <TableHead className="w-[140px]">
                                        Outcome
                                    </TableHead>
                                    <TableHead className="w-[120px]">
                                        Prompt
                                    </TableHead>
                                    <TableHead className="w-[110px]">
                                        Video
                                    </TableHead>
                                    <TableHead className="w-[100px]">
                                        Data
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {renders.map((r) => (
                                    <TableRow key={r.captionRenderId}>
                                        <TableCell className="align-top text-xs text-gray-500">
                                            {format(
                                                new Date(r.createdAt),
                                                "d MMM, HH:mm"
                                            )}
                                        </TableCell>

                                        <TableCell className="max-w-0 align-top text-xs">
                                            <div className="truncate text-primary">
                                                {r.userName ?? "—"}
                                            </div>
                                            <div className="truncate text-gray-500">
                                                {r.email ?? r.userId}
                                            </div>
                                        </TableCell>

                                        <TableCell className="max-w-0 align-top text-xs">
                                            {/* The listing was deleted after the caption
                                                shipped; the render is kept, so the row has
                                                no address/MLS#. Say so, or it reads as a
                                                blank listing rather than a deleted one. */}
                                            {r.listingDeleted ? (
                                                <div className="flex items-center gap-1 font-medium text-[#c62828]">
                                                    <Trash2 className="h-3 w-3 shrink-0" />
                                                    Listing deleted
                                                </div>
                                            ) : (
                                                <>
                                                    <div
                                                        title={r.address}
                                                        className="truncate text-primary"
                                                    >
                                                        {r.address ?? "—"}
                                                    </div>
                                                    <div className="truncate text-gray-500">
                                                        {r.mlsId
                                                            ? `MLS# ${r.mlsId}`
                                                            : "No MLS#"}
                                                    </div>
                                                </>
                                            )}
                                            <div
                                                title={r.listingId}
                                                className="truncate font-mono text-[10px] text-gray-400"
                                            >
                                                {r.listingId}
                                            </div>
                                        </TableCell>

                                        <TableCell className="align-top">
                                            {/* The denominator matters: "2 of 7 bad" reads
                                                very differently from "2 of 2". */}
                                            <div className="mb-1 text-xs text-gray-500">
                                                {r.slides.length} of {r.slideCount}{" "}
                                                slide
                                                {r.slideCount === 1 ? "" : "s"} in this
                                                video
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                {r.slides.map((s) => (
                                                    <div
                                                        key={s.slideIndex}
                                                        className="rounded border border-gray-100 p-2"
                                                    >
                                                        <div className="mb-1 flex items-center justify-between gap-2">
                                                            <span className="text-xs text-gray-500">
                                                                Slide #{s.slideIndex + 1}
                                                            </span>
                                                            <DispositionBadge
                                                                disposition={
                                                                    s.disposition
                                                                }
                                                            />
                                                        </div>
                                                        <div className="font-semibold text-primary">
                                                            {s.header || "—"}
                                                        </div>
                                                        <div className="mb-1 text-xs text-gray-500">
                                                            {s.subHeader}
                                                        </div>
                                                        {s.reasons.map((reason) => (
                                                            <ReasonChip
                                                                key={reason}
                                                                reason={reason}
                                                            />
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </TableCell>

                                        <TableCell className="align-top">
                                            <RenderOutcome slides={r.slides} />
                                        </TableCell>

                                        <TableCell className="align-top text-xs text-gray-500">
                                            <div>{r.promptVersion}</div>
                                            <div>
                                                {r.tier} · {r.cropProfile}
                                            </div>
                                        </TableCell>

                                        <TableCell className="align-top">
                                            {/* A deleted listing gets no preview at all,
                                                even if a stale video URL survived — the
                                                listing it belonged to is gone, so there is
                                                nothing to review it against. Checked first
                                                so it always wins over the Preview button. */}
                                            {r.listingDeleted ? (
                                                <span className="text-xs text-[#c62828]">
                                                    Listing deleted — no preview
                                                </span>
                                            ) : r.videoUrl ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-xs"
                                                    onClick={() => setPreview(r)}
                                                >
                                                    <Play className="mr-1 h-3 w-3" />
                                                    Preview
                                                </Button>
                                            ) : (
                                                // No render means the captions were
                                                // generated but never made it into a
                                                // video — there is nothing to watch.
                                                <span className="text-xs text-gray-400">
                                                    No render
                                                </span>
                                            )}
                                        </TableCell>

                                        <TableCell className="align-top">
                                            {/* Everything the model was given and
                                                produced. Fetched only on click. */}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-xs"
                                                onClick={() =>
                                                    setAuditId(r.captionRenderId)
                                                }
                                            >
                                                <FileJson className="mr-1 h-3 w-3" />
                                                Data
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>

            <CardFooter className="justify-center p-4 pt-0">
                <Pagination>
                    <PaginationContent>
                        <PaginationItem>
                            <PaginationPrevious
                                className={cn(
                                    page === 1 &&
                                        "pointer-events-none opacity-50"
                                )}
                                onClick={() => onPageChange(page - 1)}
                            />
                        </PaginationItem>
                        {pages.map((p) => (
                            <PaginationItem key={p}>
                                <PaginationLink
                                    isActive={p === page}
                                    onClick={() => onPageChange(p)}
                                >
                                    {p}
                                </PaginationLink>
                            </PaginationItem>
                        ))}
                        <PaginationItem>
                            <PaginationNext
                                className={cn(
                                    page === pageCount &&
                                        "pointer-events-none opacity-50"
                                )}
                                onClick={() => onPageChange(page + 1)}
                            />
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            </CardFooter>

            <Dialog
                open={preview !== null}
                onOpenChange={(open) => !open && setPreview(null)}
            >
                <DialogContent className="relative max-w-3xl bg-white">
                    {/* The shared DialogContent ships no close affordance, so add one. */}
                    <DialogClose className="absolute right-3 top-3 z-10 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-primary focus:outline-none">
                        <X className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                    </DialogClose>
                    <DialogHeader>
                        <DialogTitle className="pr-8 text-left text-sm font-semibold">
                            {preview?.address ?? "Rendered slideshow"}
                        </DialogTitle>
                        {/* Every bad caption in THIS video, so the admin knows which
                            slides to watch for rather than guessing. */}
                        <div className="flex flex-col gap-1">
                            {preview?.slides.map((s) => (
                                <p
                                    key={s.slideIndex}
                                    className="text-xs text-gray-500"
                                >
                                    <span className="font-semibold text-primary">
                                        Slide #{s.slideIndex + 1}
                                    </span>{" "}
                                    {s.header || "—"}
                                    {s.subHeader ? ` / ${s.subHeader}` : ""}{" "}
                                    <span className="text-gray-400">
                                        (
                                        {s.reasons
                                            .map((x) => REASON_LABELS[x])
                                            .join(", ")}
                                        )
                                    </span>
                                </p>
                            ))}
                        </div>
                    </DialogHeader>
                    {preview?.videoUrl && (
                        <PostVideoPlayer src={preview.videoUrl} autoPlay />
                    )}
                </DialogContent>
            </Dialog>

            <CaptionAuditDialog
                captionRenderId={auditId}
                onClose={() => setAuditId(null)}
            />
        </Card>
    );
};

export default CaptionViolationsList;
